import { useEffect, useState, useRef } from "react";
import {
  generateEntityKeyForLocalStorage,
  readDataFromLocalStorage,
} from "../utils/table";
import { getType } from "../utils/utils";
import { getDefaultSortModel } from "../constants/defaultSorting";

/**
 * Custom hook to manage the sortModel state
 * @param {string} entity - The entity name
 * @param {Object} options - The options object
 * @param {boolean} options.nestedEntity - If the entity is nested
 * @param {boolean} options.assignModal - If the entity is in a modal
 * @param {array} options.columns - Columns array for sort mapping
 * @param {Object} options.parent - Parent context for entity-specific sorting (e.g., sections)
 * @returns {Object} - The sortModel state and the setSortModel function
 */
export const useSortModel = (entity, options) => {
  const [sortModel, setSortModel] = useState([]);
  const { nestedEntity, assignModal, columns, parent } = options || {};
  const initialized = useRef(false);

  useEffect(() => {
    // Only load from localStorage once on initial mount
    if (!initialized.current) {
      const storedSortModel = readDataFromLocalStorage(
        generateEntityKeyForLocalStorage(getType(entity), "sortModel", {
          nestedEntity,
          assignModal,
        })
      );

      // Check if we have a valid sortModel in localStorage
      if (
        storedSortModel &&
        Array.isArray(storedSortModel) &&
        storedSortModel.length > 0
      ) {
        setSortModel(storedSortModel);
      } else {
        // Use getDefaultSortModel function which handles business logic mappings
        const defaultSortModel = getDefaultSortModel(entity, { parent });
        setSortModel(defaultSortModel);
      }

      initialized.current = true;
    }
  }, [entity, nestedEntity, assignModal, columns, parent]);

  return { sortModel, setSortModel };
};
